import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GROUPS = [
  { name: 'Smileys', symbol: '😀', emojis: '😀 😃 😄 😁 😆 😅 😂 🤣 😊 🙂 😉 😍 🥰 😘 😋 😎 🤩 🥳 😇 🤗 🤔 😐 😢 😭 😡 🥺 😴 😌 😮 😱 😜 😏'.split(' ') },
  { name: 'Gestures', symbol: '👍', emojis: '👍 👎 👋 🙏 👏 🙌 🤝 💪 ✌️ 🤞 👌 🫶 👐 ✋ 🤚 ☝️ 👆 👇 👉 👈 ❤️ 💕 💖 💯 🔥 ✨ ⭐ ✅ ❌'.split(' ') },
  { name: 'Things', symbol: '🎉', emojis: '🎉 🎊 🎁 🏆 🎮 🎲 🎯 ⚽ 🏏 🥇 💰 💸 💎 📱 💻 📞 📸 📝 📌 🔔 🚀 🎵 ☕ 🍕 🌈 🌹'.split(' ') },
];

const MARGIN = 10;
const GAP = 8;
export default function SupportEmojiPicker({ onSelect, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState(0);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const reposition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const viewport = window.visualViewport;
    const minTop = (viewport?.offsetTop ?? 0) + MARGIN;
    const visibleBottom = (viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight) - MARGIN;
    const viewportWidth = Math.min(document.documentElement.clientWidth, window.innerWidth);
    const width = Math.min(306, Math.max(180, viewportWidth - MARGIN * 2));
    const left = Math.max(MARGIN, Math.min(rect.left, viewportWidth - MARGIN - width));
    const above = Math.max(0, rect.top - GAP - minTop);
    const below = Math.max(0, visibleBottom - rect.bottom - GAP);
    const showAbove = above >= 230 || above >= below;
    const available = showAbove ? above : below;
    const maxHeight = Math.min(312, Math.max(92, available));
    const height = Math.min(panelRef.current?.offsetHeight || 292, maxHeight);
    const top = showAbove ? Math.max(minTop, rect.top - GAP - height) : Math.max(minTop, rect.bottom + GAP);
    setPosition({ top, left, width, maxHeight });
  }, []);

  useLayoutEffect(() => { if (open) reposition(); }, [open, group, reposition]);
  useEffect(() => {
    if (!open) return;
    const outside = event => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const escape = event => {
      if (event.key === 'Escape') {
        event.stopPropagation(); event.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const update = () => reposition();
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape, true);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape, true);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [open, reposition]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  return <div className="gp-emoji-dock">
    <button ref={buttonRef} type="button" className="gp-icon-btn"
      aria-label="Choose emoji" title="Emoji" aria-expanded={open}
      aria-haspopup="dialog" disabled={disabled}
      onClick={() => { if (!open) reposition(); setOpen(value => !value); }}>😊</button>
    {open && position && typeof document !== 'undefined' && createPortal(
      <div ref={panelRef} data-gp-support-emoji-portal="true"
        className="gp-emoji-panel" role="dialog" aria-label="Emoji picker"
        onPointerDown={event => event.stopPropagation()}
        style={{
          position: 'fixed', top: position.top, left: position.left, width: position.width,
          right: 'auto', bottom: 'auto', maxHeight: position.maxHeight,
          overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box', zIndex: 100000,
        }}>
        <header><strong>Emoji</strong><button type="button" aria-label="Close emoji picker" onClick={() => setOpen(false)}>×</button></header>
        <nav className="gp-emoji-tabs">
          {GROUPS.map((item,i) => <button type="button" key={item.name} title={item.name}
            aria-pressed={group === i} onClick={() => setGroup(i)}>{item.symbol}</button>)}
        </nav>
        <div className="gp-emoji-grid">
          {GROUPS[group].emojis.map((emoji,i) => <button type="button" key={`${emoji}-${i}`}
            aria-label={`Insert ${emoji}`} onMouseDown={event => event.preventDefault()}
            onClick={() => { onSelect(emoji); setOpen(false); }}>{emoji}</button>)}
        </div>
      </div>, document.body,
    )}
  </div>;
}
